import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { VALUELESS_OPERATORS, type ConditionGroup, type ConditionRule } from './conditions';
import { isComplete, type ConditionValue } from './conditionValues';
import { ConditionValueInput, ConditionValueText } from './ConditionValueInput';
import { useRuleFields } from './queries';
import { Select } from '../../components/Select';

/**
 * Bedingungen zusammenstellen -- F3-12, K-58.
 *
 * **Auswahlfelder und kein Textfeld.** docs/03, Abschnitt 8: kein Ausfuehren
 * beliebiger Ausdruecke. Die Felder und Vergleiche kommen vom Server
 * (`GET /automation-rules/fields`) und nicht aus einer Liste hier -- eine
 * zweite Liste liefe auseinander, sobald ein Feld dazukommt, und der Anwender
 * saehe eine Auswahl, die der Server ablehnt.
 *
 * Eine Ebene, keine Untergruppen. Der Server kann zwei (`max_depth`), aber
 * eine Oberflaeche fuer verschachtelte Bedingungen ist ein eigenes Vorhaben --
 * und wer sie braucht, baut nach docs/03 besser zwei Regeln.
 */

interface ConditionEditorProps {
  value: ConditionGroup;
  onChange: (next: ConditionGroup) => void;

  /**
   * Was eine leere Bedingung bedeutet.
   *
   * Der Satz unterscheidet sich je Aufrufer: Bei einer Regel heisst leer
   * "greift bei jedem Ausloeser", bei einem Zeitplan "nimmt jedes offene
   * Ticket". Derselbe Satz an beiden Stellen waere an einer davon falsch.
   */
  emptyHint: string;
}

export function ConditionEditor({ value, onChange, emptyHint }: ConditionEditorProps) {
  const { t } = useTranslation();
  const fields = useRuleFields();

  const [field, setField] = useState('subject');
  const [op, setOp] = useState('contains');
  const [draft, setDraft] = useState<ConditionValue | null>(null);

  const needsValue = !VALUELESS_OPERATORS.has(op);

  const add = () => {
    const rule: ConditionRule =
      needsValue && draft !== null ? { field, op, value: draft } : { field, op };
    onChange({ ...value, rules: [...value.rules, rule] });
    setDraft(null);
  };

  return (
    <div className="stack">
      <div className="row">
        <label className="field__label" htmlFor="condition-operator">
          {t('automation.joinWith')}
        </label>
        <Select
          id="condition-operator"
          className="field__control"
          value={value.operator}
          onChange={(event) => {
            onChange({ ...value, operator: event.target.value as 'and' | 'or' });
          }}
        >
          <option value="and">{t('automation.operatorAnd')}</option>
          <option value="or">{t('automation.operatorOr')}</option>
        </Select>
      </div>

      {value.rules.length === 0 ? (
        <p className="table__muted">{t('automation.noConditions')}</p>
      ) : (
        <ol className="task-list">
          {value.rules.map((rule, index) => (
            <li key={`${rule.field}-${rule.op}-${String(index)}`} className="task-list__item">
              <Badge tone="neutral">{rule.field}</Badge>
              <span>{t(`automation.op.${rule.op}`)}</span>
              <strong>
                <ConditionValueText field={rule.field} value={rule.value} />
              </strong>
              <Button
                variant="ghost"
                onClick={() => {
                  onChange({
                    ...value,
                    rules: value.rules.filter((_, position) => position !== index),
                  });
                }}
              >
                {t('masterdata.delete')}
              </Button>
            </li>
          ))}
        </ol>
      )}

      <div className="row">
        <Select
          className="field__control"
          aria-label={t('automation.conditionField')}
          value={field}
          onChange={(event) => {
            setField(event.target.value);
            setDraft(null);
          }}
        >
          {(fields.data?.fields ?? []).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>

        <Select
          className="field__control"
          aria-label={t('automation.conditionOperator')}
          value={op}
          onChange={(event) => {
            setOp(event.target.value);
            setDraft(null);
          }}
        >
          {(fields.data?.operators ?? []).map((name) => (
            <option key={name} value={name}>
              {t(`automation.op.${name}`)}
            </option>
          ))}
        </Select>

        {needsValue ? (
          // Neu aufgebaut, sobald Feld oder Vergleich wechseln: Ein Kunde, der
          // unter "customer_id" gewaehlt war, ist unter "subject" kein Wert.
          <ConditionValueInput
            key={`${field}:${op}`}
            field={field}
            op={op}
            value={draft}
            onChange={setDraft}
          />
        ) : null}

        <Button variant="secondary" disabled={needsValue && !isComplete(draft)} onClick={add}>
          {t('automation.addCondition')}
        </Button>
      </div>

      <p className="field__hint">{emptyHint}</p>
    </div>
  );
}
